import { LeadListScope } from '@up2stay/up2promo-api-client';
import { SegmentedControl, cn } from '@up2stay/up2promo-ui';
import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';

export type LeadsScopeCounts = {
  mine: number;
  workspace: number;
  othersPrivate: number;
};

type LeadsScopeTabsProps = {
  value: LeadListScope;
  onChange: (scope: LeadListScope) => void;
  counts: LeadsScopeCounts;
  canSeeOthersPrivate: boolean;
  labels: {
    mine: string;
    workspace: string;
    othersPrivate: string;
  };
  /** Short label for others-private on mobile (mock: "Privat"). */
  othersPrivateShortLabel?: string;
  fullWidth?: boolean;
  className?: string;
};

function ScopeLabel({
  label,
  count,
  lock,
}: {
  label: string;
  count: number;
  lock?: boolean;
}) {
  return (
    <>
      {lock ? (
        <Lock className="size-[13px] shrink-0 opacity-70" aria-hidden />
      ) : null}
      <span>{label}</span>
      <span className="font-mono text-[11px] text-muted-foreground">
        {count}
      </span>
    </>
  );
}

/** Meine · Workspace · Privat anderer — SegmentedControl with mono counts. */
export function LeadsScopeTabs({
  value,
  onChange,
  counts,
  canSeeOthersPrivate,
  labels,
  othersPrivateShortLabel,
  fullWidth = false,
  className,
}: LeadsScopeTabsProps) {
  const othersLabel = othersPrivateShortLabel ?? labels.othersPrivate;

  const options: Array<{
    value: LeadListScope;
    label: ReactNode;
  }> = [
    {
      value: LeadListScope.MINE,
      label: <ScopeLabel label={labels.mine} count={counts.mine} />,
    },
    {
      value: LeadListScope.WORKSPACE,
      label: <ScopeLabel label={labels.workspace} count={counts.workspace} />,
    },
  ];

  if (canSeeOthersPrivate) {
    options.push({
      value: LeadListScope.OTHERS_PRIVATE,
      label: (
        <ScopeLabel
          label={fullWidth ? othersLabel : labels.othersPrivate}
          count={counts.othersPrivate}
          lock
        />
      ),
    });
  }

  return (
    <SegmentedControl
      ariaLabel={labels.workspace}
      value={value}
      onChange={onChange}
      options={options}
      fullWidth={fullWidth}
      className={className}
      // Content-based widths: equal thirds clip „Arbeitsbereich“ on phones.
      itemClassName={cn(
        'h-auto gap-1.5 px-3 py-[7px] text-[12.5px]',
        fullWidth && 'basis-auto',
      )}
    />
  );
}
